/**
 * Krok UXF5 bod 6 (nalez A7 / #18) — „CO S TIM CLOVEKEM UDELAT DAL".
 *
 * Re-audit GPT (10. 10. 2026, dukazni snimek E09): v seznamu klientu pribyly
 * role, e-mail i zakazka, ale makler z nej stale nepozna, komu zavolat —
 * chybi POSLEDNI KONTAKT a NEJBLIZSI PLANOVANA AKCE.
 *
 * ROZHODNUTI UXF5-KONTAKT: oba udaje se skladaji z toho, co uz REOS
 * O KLIENTOVI VI — z ukolu (`crm_tasks`, pole `partyId`) a z udalosti
 * kalendare (`crm_calendar_events`, pole `partyId`). Nic se nedomysli:
 *
 *  - „posledni kontakt" = nejnovejsi DOKONCENY ukol nebo JIZ PROBEHLA
 *    udalost u teho klienta. Kdyz nic takove neni, rika se to vyslovne
 *    („Zatím nekontaktován"), ne pomlckou.
 *  - „dalsi akce" = nejblizsi OTEVRENY ukol s terminem nebo BUDOUCI
 *    udalost — cokoli z toho prijde driv.
 *
 * Co se tim NESLIBUJE: tohle neni historie komunikace. `GET
 * /v1/communication/messages?partyId=` by rekla, kdy klientovi naposledy
 * odesel e-mail, ale je to dotaz NA KAZDEHO KLIENTA zvlast (20 dotazu na
 * stranku seznamu pri kazdem psani do hledani) a jeji DTO je v OpenAPI
 * volne (`additionalProperties`). Ukoly a udalosti chodi JEDNOU DAVKOU,
 * kterou uz obrazovky adminu delaji (`/zakazky`, Dnešek). Pokud bude pozdeji
 * potreba i e-mail, patri to do teto funkce, ne do druhe.
 *
 * Funkce je ciste vypocetni (bez `fetch`, bez `Date.now()`), aby se na ni
 * dal napsat test bez prohlizece a bez site.
 */

import { isTaskOpen } from './task-board.js';

/** Jen to, co vypocet potrebuje — ne cely `Task` z API. */
export interface ClientActivityTask {
  readonly partyId?: string | null | undefined;
  readonly title: string;
  readonly status: string;
  readonly dueAt?: string | null | undefined;
  readonly completedAt?: string | null | undefined;
}

/** Jen to, co vypocet potrebuje — ne cela `CalendarEvent` z API. */
export interface ClientActivityEvent {
  readonly partyId?: string | null | undefined;
  readonly title: string;
  readonly startsAt: string;
}

export interface ClientActivity {
  /** ISO casu posledniho doloziteneho kontaktu, nebo `null` = zadny. */
  readonly lastContactAt: string | null;
  /** Nejblizsi planovana akce, nebo `null` = nic naplanovano. */
  readonly nextAction: { readonly title: string; readonly at: string } | null;
}

function time(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string' || iso === '') return null;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? null : value;
}

export function clientActivityByParty(input: {
  readonly tasks: readonly ClientActivityTask[];
  readonly events: readonly ClientActivityEvent[];
  readonly now: Date;
}): ReadonlyMap<string, ClientActivity> {
  const nowMs = input.now.getTime();
  const last = new Map<string, number>();
  const next = new Map<string, { readonly title: string; readonly at: string; readonly ms: number }>();

  const noteLast = (partyId: string, iso: string | null | undefined): void => {
    const ms = time(iso);
    if (ms === null || ms > nowMs) return;
    const current = last.get(partyId);
    if (current === undefined || ms > current) last.set(partyId, ms);
  };
  const noteNext = (partyId: string, title: string, iso: string | null | undefined): void => {
    const ms = time(iso);
    if (ms === null || ms < nowMs) return;
    const current = next.get(partyId);
    if (current === undefined || ms < current.ms) next.set(partyId, { title, at: iso as string, ms });
  };

  for (const task of input.tasks) {
    const partyId = task.partyId ?? null;
    if (partyId === null || partyId === '') continue;
    if (isTaskOpen(task.status)) noteNext(partyId, task.title, task.dueAt);
    else noteLast(partyId, task.completedAt);
  }

  for (const event of input.events) {
    const partyId = event.partyId ?? null;
    if (partyId === null || partyId === '') continue;
    noteLast(partyId, event.startsAt);
    noteNext(partyId, event.title, event.startsAt);
  }

  const partyIds = new Set<string>([...last.keys(), ...next.keys()]);
  return new Map(
    [...partyIds].map((partyId) => {
      const lastMs = last.get(partyId);
      const nextItem = next.get(partyId);
      return [
        partyId,
        {
          lastContactAt: lastMs === undefined ? null : new Date(lastMs).toISOString(),
          nextAction: nextItem === undefined ? null : { title: nextItem.title, at: nextItem.at },
        },
      ];
    }),
  );
}
